import React from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'

import { adminReimbursementRequests } from 'rails_routes'
import { RadioGroup, TextInput } from 'slab'
import { useGetBrokersMembersMemberIdEnrollmentsHasCoveragesOrPayments } from 'utils/web_api'
import { type BrokersEnrollmentWithPaymentsOrCoverages } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { AdminReimbursementSubmissionButtons } from './AdminReimbursementSubmissionButtons'
import { ReimbursementAmountLimit } from './ReimbursementAmountLimit'
import { TransactionDateInput } from './ReimbursementDateInput'
import { ReimbursementEnrollmentSelections } from './ReimbursementEnrollmentSelections'
import { ReimbursementFormDropdown } from './ReimbursementFormDropdown'
import {
  type AdminReimbursementFormInput,
  type DropdownOption,
  ReimbursementPurpose,
  type ReimbursementTypeOption,
  RejectionReasons,
} from './types'
import { formatMoney, getCSRFToken } from '../../utils'
import { handleAmountChange } from '../../utils/reimbursement_utils'
import NoteEditor from '../brokers/needs_attention/NoteEditor'

interface AdminOneOffReimbursementProps {
  purpose?: string
  amount?: string
  memberId: string
  transactionDate: Date
  maxICHRABalance: number
  maxICHRABalanceLastYear: number
  maxHSABalance: number
  type: ReimbursementTypeOption
  reimbursementId: string
  isThisEmployeeInactive: boolean
}

export const AdminOneOffReimbursementsFormImpl: React.FC<
  AdminOneOffReimbursementProps
> = ({
  purpose,
  amount,
  memberId,
  transactionDate,
  maxICHRABalance,
  maxICHRABalanceLastYear,
  maxHSABalance,
  reimbursementId,
  type = 'ichra',
  isThisEmployeeInactive = false,
}) => {
  const { assign } = useWindowLocation()

  const [noteContent, setNoteContent] = React.useState<string>('')
  const { data: enrollments, isLoading: enrollmentsLoading, error: enrollmentsError } =
    useGetBrokersMembersMemberIdEnrollmentsHasCoveragesOrPayments(memberId)

  const {
    control,
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors },
  } = useForm<AdminReimbursementFormInput>({
    mode: 'onChange',
    defaultValues: {
      purpose: purpose || '',
      reason: '',
      amount: amount || '',
      transaction_date: transactionDate
        ? new Date(transactionDate).toISOString().slice(0, 10)
        : undefined,
      id: reimbursementId,
    },
  })

  const selectedPurpose = watch('purpose')
  const selectedEnrollmentId = watch('insurance_enrollment_id')
  const selectedApproved = watch('approved')
  const selectedTransactionDate = watch('transaction_date')

  const onSubmit: SubmitHandler<AdminReimbursementFormInput> = async (data) => {
    try {
      const transformedData = {
        ...data,
        amount: data.amount.replace(/,/g, ''),
        note: noteContent,
      }
      const payload = { reimbursement: transformedData }
      const url = adminReimbursementRequests.update({ id: reimbursementId })
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || '',
        },
        body: JSON.stringify(payload),
      })

      const json = await response.json()

      if (response.ok) {
        assign(json.redirect_to)
      } else {
        alert('Error submitting form.')
      }
    } catch (e) {
      alert(`Error submitting form: ${e}`)
    }
  }

  const purposeOptions: DropdownOption[] = [
    {
      label: 'QME',
      value: ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE,
    },
    {
      label: 'Insurance premium',
      value: ReimbursementPurpose.PREMIUM,
    },
  ]

  const rejectionReasonOptions: DropdownOption[] = [
    {
      label: 'QME Ineligible',
      value: RejectionReasons.QME_INELIGIBLE,
    },
    {
      label: 'Duplicate',
      value: RejectionReasons.DUPLICATE,
    },
    {
      label: 'Outside coverage window',
      value: RejectionReasons.OUTSIDE_COVERAGE_WINDOW,
    },
    {
      label: 'QME Ineligible (Need LMN)',
      value: RejectionReasons.QME_INELIGIBLE_NEED_LMN,
    },
    {
      label: 'Other (please specify in note))',
      value: RejectionReasons.OTHER,
    },
  ]

  const reimbursementOnlyApplies = (
    enrollment: BrokersEnrollmentWithPaymentsOrCoverages
  ) => {
    if (!enrollment.reimbursement_only_at) {
      return false
    }

    const reimbursementOnlyCutoff = new Date(enrollment.reimbursement_only_at)
    return new Date(selectedTransactionDate) >= reimbursementOnlyCutoff
  }

  const fetchMaxICHRABalance = (inputDate: string | null) => {
    if (inputDate) {
      const date = new Date(inputDate).toISOString()
      const year = new Date(date).getUTCFullYear()
      const currentYear = new Date().getFullYear()
      return year === currentYear || year < currentYear - 1
        ? maxICHRABalance
        : maxICHRABalanceLastYear
    } else {
      return maxICHRABalance
    }
  }

  const getMaximumAmount = () => {
    if (type === 'hsa') {
      return maxHSABalance
    } else if (selectedPurpose === ReimbursementPurpose.PREMIUM) {
      const selectedEnrollment = enrollments?.filter(
        (e) => e.id === selectedEnrollmentId
      )[0]

      return selectedEnrollment && reimbursementOnlyApplies(selectedEnrollment)
        ? fetchMaxICHRABalance(selectedEnrollment.coverage_start_date || '')
        : Math.max(
          parseFloat(selectedEnrollment?.max_reimbursable_amount?.value || '0'),
          0
        )
    } else {
      return fetchMaxICHRABalance(selectedTransactionDate)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='mt-6'>
        <Controller
          name='purpose'
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <ReimbursementFormDropdown
              value={value}
              onChange={onChange}
              dropdownOptions={purposeOptions}
              label='Purpose'
            />
          )}
        />
      </div>
      <div className='mt-6'>
        <Controller
          name='transaction_date'
          control={control}
          render={() => (
            <TransactionDateInput<AdminReimbursementFormInput>
              error={errors.transaction_date}
              minDate={undefined}
              register={register}
              transactionDate={selectedTransactionDate}
              purpose={selectedPurpose}
              setValue={setValue}
            />
          )}
        />
      </div>
      {selectedPurpose === ReimbursementPurpose.PREMIUM && (
        <div className='mt-6'>
          <RadioGroup
            label='Enrollments'
            error={
              errors.insurance_enrollment_id?.type === 'required' &&
              'Enrollment selection is required.'
            }
            gap={3}
            name='insurance_enrollment_id'
          >
            {enrollmentsLoading
              ? (
                <div className='mt-3 flex items-center space-x-2'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900' />
                  <p className='text-sm text-gray-500'>Loading enrollments...</p>
                </div>
                )
              : enrollmentsError
                ? (
                  <p className='mt-3 font-normal text-sm text-red-600'>
                    Error loading enrollments. Please try again.
                  </p>
                  )
                : enrollments && enrollments.length > 0
                  ? (
                    <Controller
                      name='insurance_enrollment_id'
                      control={control}
                      rules={{ required: selectedApproved }}
                      render={({ field: { onChange } }) => (
                        <ReimbursementEnrollmentSelections
                          enrollments={enrollments}
                          onChange={onChange}
                          selectedEnrollmentId={selectedEnrollmentId}
                          selectedTransactionDate={
                      errors.transaction_date
                        ? undefined
                        : selectedTransactionDate
                    }
                        />
                      )}
                    />
                    )
                  : (
                    <p className='mt-3 font-normal text-sm italic'>
                      No enrollments found. Create an enrollment for this member in
                      the Broker Dashboard.
                    </p>
                    )}
          </RadioGroup>
        </div>
      )}
      {((selectedPurpose === ReimbursementPurpose.PREMIUM &&
        selectedEnrollmentId) ||
        selectedPurpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) && (
          <ReimbursementAmountLimit
            enrollments={enrollments || []}
            getMaximumAmount={getMaximumAmount}
            reimbursementType={type}
            selectedEnrollmentId={selectedEnrollmentId}
            selectedPurpose={selectedPurpose}
            reimbursementOnlyApplies={reimbursementOnlyApplies}
          />
      )}
      {((selectedPurpose === ReimbursementPurpose.PREMIUM &&
        selectedEnrollmentId) ||
        selectedPurpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) && (
          <div className='mt-6'>
            <Controller
              name='amount'
              control={control}
              rules={{
                required: selectedApproved && 'Amount field is required',
                validate: (value) => {
                  if (selectedApproved) {
                    if (parseFloat(value) > getMaximumAmount()) {
                      return `Amount cannot exceed ${formatMoney(getMaximumAmount())}`
                    }
                  }

                  return true
                }
              }}
              render={({ field }) => (
                <div className='mt-4'>
                  <TextInput
                    error={errors.amount?.message}
                    label='Reimbursement Amount'
                    inputMode='numeric'
                    {...field}
                    onChange={(e) => {
                      handleAmountChange(e, field.onChange)
                    }}
                  />
                </div>
              )}
            />
          </div>
      )}
      <div className='mt-6'>
        <Controller
          name='reason'
          control={control}
          rules={{ required: selectedApproved === false && 'Rejection reason required when rejecting reimbursement.' }}
          render={({ field: { value, onChange } }) => (
            <ReimbursementFormDropdown
              error={errors.reason?.message}
              value={value ?? null}
              onChange={onChange}
              dropdownOptions={rejectionReasonOptions}
              label='Rejection Reason'
            />
          )}
        />
      </div>

      <div className='mt-6'>
        <div className='space-y-2'>
          <label className='label-default'>
            Add Note (Optional)
          </label>
          <NoteEditor setValue={setNoteContent} />
        </div>
      </div>

      <div className='mt-6 my-4'>
        <Controller
          name='approved'
          control={control}
          render={({ field: { onChange } }) => (
            <AdminReimbursementSubmissionButtons
              onChange={onChange}
              errors={errors}
              isThisEmployeeInactive={isThisEmployeeInactive}
            />
          )}
        />
      </div>
    </form>
  )
}

export const AdminOneOffReimbursementsForm = (props: AdminOneOffReimbursementProps) => (
  <AdminOneOffReimbursementsFormImpl {...props} />
)
